import {
  buildPageReloadConfirmMessage,
  getPageReloadConfirmTitle,
  isPageReloadShortcut,
} from '@/common/utils/pageReloadGuard'
import {
  registerSkipNextPageLeaveConfirm,
  unregisterSkipNextPageLeaveConfirm,
} from '@/common/utils/pageLeaveGuardBridge'
import { showConfirmDialog } from '@/services/messages/dialog-manager'
import { t } from '@/common/utils/translations'

type UsePageReloadGuardOptions = {
  shouldConfirmReload: () => boolean
  getHasOpenForms: () => boolean
  onBeforeUnload?: () => void
}

export function usePageReloadGuard(options: UsePageReloadGuardOptions) {
  let skipNextBeforeUnloadConfirm = false

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    options.onBeforeUnload?.()

    if (skipNextBeforeUnloadConfirm) {
      skipNextBeforeUnloadConfirm = false
      return
    }

    if (!options.shouldConfirmReload()) return

    event.preventDefault()
  }

  const confirmAndReload = async () => {
    if (!options.shouldConfirmReload()) {
      skipNextBeforeUnloadConfirm = true
      window.location.reload()
      return
    }

    const agreed = await showConfirmDialog({
      title: getPageReloadConfirmTitle(),
      message: buildPageReloadConfirmMessage(options.getHasOpenForms()),
      confirmText: t('dialogs.reload'),
      cancelText: t('dialogs.cancel'),
    })

    if (!agreed) return

    skipNextBeforeUnloadConfirm = true
    window.location.reload()
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isPageReloadShortcut(event) || event.defaultPrevented || event.repeat) return

    event.preventDefault()
    event.stopPropagation()
    void confirmAndReload()
  }

  const attachPageReloadGuard = () => {
    registerSkipNextPageLeaveConfirm(() => {
      skipNextBeforeUnloadConfirm = true
    })
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('keydown', handleKeyDown, true)
  }

  const detachPageReloadGuard = () => {
    unregisterSkipNextPageLeaveConfirm()
    window.removeEventListener('beforeunload', handleBeforeUnload)
    window.removeEventListener('keydown', handleKeyDown, true)
  }

  return {
    attachPageReloadGuard,
    detachPageReloadGuard,
    skipNextPageLeaveConfirm: () => {
      skipNextBeforeUnloadConfirm = true
    },
  }
}
